<!--

 -->

<template>
  <reports-table
    module="time"
    name="employee"
    :filters="serializedFilters"
    :details="details"
    :column-count="6"
  >
    <template #default="{generateReport}">
      <oxd-table-filter :filter-title="$t('time.productivity_report')">
        <template #toggleOptions>
          <report-download-csv
            name="employee"
            module="time"
            :prefetch="false"
            :details="details"
            :filters="serializedFilters"
            :column-count="6"
          />
        </template>
        <oxd-form @submit-valid="generateReport">
          <oxd-form-row>
            <oxd-grid :cols="2" class="gsl-hrm-full-width-grid">
              <oxd-grid-item>
                <employee-autocomplete
                  v-model="filters.employee"
                  :rules="rules.employee"
                  :params="{
                    includeEmployees: 'currentAndPast',
                  }"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-form-row>
            <oxd-grid :cols="2" class="gsl-hrm-full-width-grid">
              <oxd-grid-item>
                <project-autocomplete
                  v-model="filters.project"
                  :rules="rules.project"
                  :label="$t('time.project_name')"
                  :only-allowed="false"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <activity-dropdown
                  v-model="filters.activity"
                  :label="$t('time.activity_name')"
                  :project-id="filters.project && filters.project.id"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-form-row>
            <oxd-grid :cols="4" class="gsl-hrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.dateRange"
                  type="select"
                  :label="$t('time.project_date_range')"
                  :options="dateRange"
                />
              </oxd-grid-item>
              <oxd-grid-item class="gsl-hrm-switch-filter --span-column-2">
                <oxd-text class="gsl-hrm-switch-filter-text" tag="p">
                  {{ $t('time.only_include_approved_timesheets') }}
                </oxd-text>
                <oxd-switch-input v-model="filters.timesheetState" />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-form-row v-if="filters?.dateRange?.id == 5">
            <oxd-grid :cols="4" class="gsl-hrm-full-width-grid">
              <oxd-grid-item>
                <date-input
                  v-model="filters.fromDate"
                  :placeholder="$t('general.from')"
                  :rules="rules.fromDate"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <date-input
                  v-model="filters.toDate"
                  :placeholder="$t('general.to')"
                  :rules="rules.toDate"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-divider />

          <oxd-form-actions>
            <required-text />
            <oxd-button
              type="submit"
              display-type="secondary"
              :label="$t('general.view')"
            />
          </oxd-form-actions>
        </oxd-form>
      </oxd-table-filter>
      <br />
    </template>

    <template #footer="{data}">
      {{ $t('time.total_duration') }}:
      {{ data.meta ? data.meta.sum.label : '0.00' }}
    </template>
  </reports-table>
</template>

<script>
import {computed, ref} from 'vue';
import {
  validSelection,
  validDateFormat,
  endDateShouldBeAfterStartDate,
  startDateShouldBeBeforeEndDate,
} from '@/core/util/validation/rules';
import ReportsTable from '@/core/components/table/ReportsTable';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import ActivityDropdown from '@/orangehrmTimePlugin/components/ActivityDropdown.vue';
import ProjectAutocomplete from '@/orangehrmTimePlugin/components/ProjectAutocomplete.vue';
import usei18n from '@/core/util/composable/usei18n';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {OxdSwitchInput} from '@ohrm/oxd';
import ReportCSVDownload from '@/core/components/table/ReportCSVDownload.vue';

const defaultFilters = {
  employee: null,
  project: null,
  activity: null,
  dateRange: null,
  fromDate: null,
  toDate: null,
  timesheetState: false,
};

export default {
  components: {
    'reports-table': ReportsTable,
    'oxd-switch-input': OxdSwitchInput,
    'activity-dropdown': ActivityDropdown,
    'project-autocomplete': ProjectAutocomplete,
    'employee-autocomplete': EmployeeAutocomplete,
    'report-download-csv': ReportCSVDownload,
  },

  setup() {
    const filters = ref({...defaultFilters});
    const {$t} = usei18n();
    const {userDateFormat} = useDateFormat();

    const rules = {
      project: [validSelection],
      employee: [validSelection],
      fromDate: [
        validDateFormat(userDateFormat),
        startDateShouldBeBeforeEndDate(
          () => filters.value.toDate,
          $t('general.from_date_should_be_before_to_date'),
          {allowSameDate: true},
        ),
      ],
      toDate: [
        validDateFormat(userDateFormat),
        endDateShouldBeAfterStartDate(
          () => filters.value.fromDate,
          $t('general.to_date_should_be_after_from_date'),
          {allowSameDate: true},
        ),
      ],
    };

    const serializedFilters = computed(() => {
      return {
        empNumber: filters.value.employee?.id,
        projectId: filters.value.project?.id,
        activityId: filters.value.activity?.id,
        fromDate: filters.value.fromDate,
        toDate: filters.value.toDate,
        timesheetState: filters.value.timesheetState ? 'onlyApproved' : 'all',
      };
    });
    const details = computed(() => {
      return {
        employeeName: filters.value.employee?.label,
        projectName: filters.value.project?.label,
        activityName: filters.value.activity?.label,
      };
    });

    return {
      rules,
      filters,
      serializedFilters,
      details,
    };
  },

  data() {
    return {
      dateRange: [
        {id: 0, label: this.$t('general.last_1_month')},
        {id: 1, label: this.$t('general.last_2_month')},
        {id: 2, label: this.$t('general.last_3_month')},
        {id: 3, label: this.$t('general.last_6_month')},
        {id: 4, label: this.$t('general.last_year')},
        {id: 5, label: this.$t('general.custom')},
      ],
    };
  },
  watch: {
    'filters.dateRange'(dateRangeVal) {
      const currentDate = new Date();
      let fromDate = new Date(currentDate);

      if (dateRangeVal.id === 0) {
        fromDate.setMonth(fromDate.getMonth() - 1);
      } else if (dateRangeVal.id === 1) {
        fromDate.setMonth(fromDate.getMonth() - 2);
      } else if (dateRangeVal.id === 2) {
        fromDate.setMonth(fromDate.getMonth() - 3);
      } else if (dateRangeVal.id === 3) {
        fromDate.setMonth(fromDate.getMonth() - 6);
      } else if (dateRangeVal.id === 4) {
        fromDate.setFullYear(fromDate.getFullYear() - 1);
      } else if (dateRangeVal.id === 5) {
        this.filters.fromDate = null;
        this.filters.toDate = null;
        return;
      }

      this.filters.fromDate = fromDate.toISOString().split('T')[0];
      this.filters.toDate = currentDate.toISOString().split('T')[0];
    },
  },
};
</script>

<style src="./time-reports.scss" lang="scss" scoped></style>
